import { matchSorter } from "match-sorter";
import { type ComponentProps, useState } from "react";

import DefaultTable from "./DefaultTable";

type DefaultTableProps = ComponentProps<typeof DefaultTable>;
type TableRowData = DefaultTableProps["rows"][number];

interface TableHeader {
	key?: string;
	value?: string;
	getSortKey?: (row: TableRowData) => number;
}

interface TableDetails {
	take: number;
	page: number;
	orderBy: string;
	order: DefaultTableProps["order"];
}

interface TableProps {
	rows: { id: string | number }[];
	searchValue?: string;
	sortKey: string;
	defaultOrderBy?: string;
	headers: TableHeader[];
	[key: string]: unknown;
}

export default function Table({ rows: inputRows, ...props }: TableProps) {
	const rows = inputRows as TableRowData[];
	const [details, setDetails] = useState<TableDetails>({
		take: 10,
		page: 0,
		orderBy: "",
		order: "DESC",
	});

	const getFilteredRows = () => {
		let filteredRows = rows;
		if (props.searchValue) {
			filteredRows = matchSorter(rows, props.searchValue, {
				keys: [props.sortKey],
			});
		}

		const sortKey = details.orderBy || props.defaultOrderBy;

		const header = props.headers.find(({ value }) => value === sortKey);
		if (!header) {
			return filteredRows;
		}
		return filteredRows.sort((a, b) => {
			const aSortKey = header.getSortKey
				? header.getSortKey(a)
				: (a[sortKey as string] as number);
			const bSortKey = header.getSortKey
				? header.getSortKey(b)
				: (b[sortKey as string] as number);

			if (details.order === "ASC") {
				return aSortKey - bSortKey;
			}
			return bSortKey - aSortKey;
		});
	};

	const filteredRows = getFilteredRows();
	const tableRows = filteredRows.slice(
		details.page * details.take,
		details.page * details.take + details.take,
	);

	return (
		<DefaultTable
			{...(props as Partial<DefaultTableProps>)}
			{...details}
			count={filteredRows.length}
			rows={tableRows}
			setDetails={(newDetails: Partial<TableDetails>) =>
				setDetails({
					...details,
					...newDetails,
				})
			}
		/>
	);
}
